import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Sparkles } from "lucide-react";
import { listKbArticles } from "@/lib/kb.functions";
import { buildTitle, clampDescription } from "@/lib/seo-quality";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";
import { AdSlot } from "@/components/AdSlot";

type Card = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_description: string | null;
  cluster: string | null;
  focus_keyword: string | null;
  cover_url: string | null;
  reading_minutes: number | null;
  published_at: string | null;
};

export const Route = createFileRoute("/kunskap/")({
  loader: async () => {
    const { articles } = await listKbArticles();
    return { articles: (articles ?? []) as Card[] };
  },
  head: ({ loaderData }) => {
    const count = loaderData?.articles.length ?? 0;
    const title = buildTitle(`Kunskapsbank husbil & husvagn – ${count} djupguider`);
    const description = clampDescription(
      `${count} faktagranskade artiklar om husbil och husvagn: priser, teknik, fukt, körkort och vinterdrift – skrivna av verkstaden i Kalix med 20 års erfarenhet.`,
    );
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      ],
      links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/kunskap" }],
    };
  },
  component: KunskapIndex,
});

function KunskapIndex() {
  const { articles } = Route.useLoaderData() as { articles: Card[] };
  const clusters = [...new Set(articles.map((a) => a.cluster).filter(Boolean))] as string[];

  return (
    <section className="container-edge max-w-6xl py-12 md:py-20">
      <p className="eyebrow">Kunskapsbank</p>
      <h1 className="mt-3 font-display text-4xl md:text-6xl">
        Allt vi vet om <span className="font-italic-display text-accent">husbil & husvagn</span>.
      </h1>
      <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
        Djupartiklar byggda på verkstadens vardag i Kalix – konkreta priser, mätvärden och
        felfrekvenser. Varje artikel går igenom vår kvalitetsgrind innan den publiceras.
      </p>

      {clusters.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {clusters.map((c) => (
            <a
              key={c}
              href={`#${encodeURIComponent(c.toLowerCase().replace(/\s+/g, "-"))}`}
              className="rounded-full border border-border bg-card px-4 py-2 text-xs font-medium hover:border-accent hover:text-accent"
            >
              {c}
            </a>
          ))}
        </div>
      )}

      {articles.length === 0 && (
        <p className="mt-12 rounded-3xl border border-border bg-card p-8 text-sm text-muted-foreground">
          Kunskapsbanken fylls på – de första artiklarna publiceras inom kort.
        </p>
      )}

      {clusters.map((cluster) => (
        <div key={cluster} id={cluster.toLowerCase().replace(/\s+/g, "-")} className="mt-14 scroll-mt-24">
          <h2 className="flex items-center gap-2 font-display text-2xl md:text-3xl">
            <BookOpen size={20} className="text-accent" /> {cluster}
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles
              .filter((a) => a.cluster === cluster)
              .map((a) => (
                <Link
                  key={a.id}
                  to="/kunskap/$slug"
                  params={{ slug: a.slug }}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:border-accent/60 soft-shadow"
                >
                  {a.cover_url && (
                    <img
                      src={a.cover_url}
                      srcSet={imgSrcSet(a.cover_url)}
                      sizes={CARD_SIZES}
                      alt={a.title}
                      width={800}
                      height={400}
                      loading="lazy"
                      decoding="async"
                      className="h-40 w-full object-cover transition group-hover:scale-[1.02]"
                    />
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-lg leading-snug group-hover:text-accent">{a.title}</h3>
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                      {a.excerpt ?? a.meta_description}
                    </p>
                    <p className="mt-4 text-xs text-muted-foreground">
                      {a.reading_minutes ?? 6} min läsning
                    </p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      ))}

      <AdSlot variant="display-horizontal" label="Annons" />

      <div className="mt-16 rounded-3xl border border-border bg-secondary/40 p-6 md:p-8">
        <p className="flex items-center gap-2 font-display text-xl">
          <Sparkles size={18} className="text-accent" /> Hittar du inte svaret?
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Fråga oss direkt eller ställ frågan i communityn – vi svarar från verkstaden.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/kontakt" className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Kontakta oss
          </Link>
          <Link to="/forum" className="rounded-full border border-border bg-card px-5 py-3 text-sm font-medium hover:bg-secondary">
            Till forumet
          </Link>
        </div>
      </div>
    </section>
  );
}
